import {
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import { useQuery, useMutation } from "@apollo/client";
import {
  CREATE_RIGHT,
  UPDATE_RIGHT,
  ALL_MODULES,
} from "../../../data/RoleManagementData";
import swal from "sweetalert";
import Swal from "sweetalert2";
import { useState, useEffect } from "react";
import Header from "../../../components/Header";
import { roleManagemenUrl } from "../../../config";
import { fetchJsonDynamic } from "../../../data/DynamicServiceNew/jsondynamicservice";

const base_url = roleManagemenUrl.uri;

const RightsForm = (props) => {
  const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));
  const userId = parseInt(decodedToken.Id);
  const companyId = parseInt(decodedToken.CompanyId);

  const {
    loading: modulesLoading,
    error: modulesError,
    data: modulesData,
  } = useQuery(ALL_MODULES);

  const [moduleName, setModuleName] = useState("");
  const [allRightsData, setAllRightsData] = useState([]);
  const [usedDisplayOrders, setUsedDisplayOrders] = useState([]);
  const [suggestedOrder, setSuggestedOrder] = useState(1);

  const [createRight] = useMutation(CREATE_RIGHT, {
    onCompleted: () => {
      props.onClose();
      props.onAction();
      swal("Success!", "Right has been created successfully", "success");
    },
    onError: () => {
      swal("Error!", "Unable to create right, try again later", "error");
    },
  });

  const [updateRight] = useMutation(UPDATE_RIGHT, {
    onCompleted: () => {
      props.onClose();
      props.onAction();
      swal("Success!", "Right has been updated successfully", "success");
    },
    onError: () => {
      swal("Error!", "Unable to update right, try again later", "error");
    },
  });

  const initialValues = {
    title: props.data ? props.data.Title : "",
    icon: props.data ? props.data.Icon : "",
    action: props.data ? props.data.Action : "",
    moduleId: props.data ? props.data.ModuleId : "",
    module: props.data ? props.data.Module : "",
    displayOrder: props.data ? props.data.DisplayOrder ?? "" : "",
    companyId: props.data ? props.data.CompanyId : "",
  };

  const validationSchema = yup.object().shape({
    title: yup.string().required("Title is required"),
    icon: yup.string().required("Icon is required"),
    action: yup.string().required("Action is required"),
    moduleId: yup.number().required("Module ID is required"),
  });

  // Fetch all rights once, used to work out per-module display order usage
  useEffect(() => {
    async function fetchAllRights() {
      try {
        const response = await fetchJsonDynamic("allRights", null, null, base_url);
        if (response?.results) {
          setAllRightsData(JSON.parse(response.results) || []);
        }
      } catch (error) {
        console.error("Error fetching rights:", error);
      }
    }
    fetchAllRights();
  }, []);

  // Recompute used/suggested order whenever the module or rights list changes
  const computeUsedOrders = (moduleId, rightsList) => {
    if (!moduleId || !rightsList.length) {
      setUsedDisplayOrders([]);
      setSuggestedOrder(1);
      return;
    }
    const siblings = rightsList.filter(
      (r) =>
        r.ModuleId === moduleId &&
        (!props.isEditing || r.Id !== props.data.Id) // exclude self when editing
    );
    const used = siblings
      .map((r) => r.DisplayOrder)
      .filter((n) => n !== null && n !== undefined)
      .sort((a, b) => a - b);

    setUsedDisplayOrders(used);
    setSuggestedOrder(used.length ? Math.max(...used) + 1 : 1);
  };

  // If editing, and rights list has already loaded, compute used orders for the existing module
  useEffect(() => {
    if (initialValues.moduleId && allRightsData.length) {
      computeUsedOrders(initialValues.moduleId, allRightsData);
    }
  }, [allRightsData]);

  const handleModuleChange = (event, setFieldValue) => {
    const selectedModuleId = event.target.value;
    const allModulesArr = JSON.parse(modulesData.allModules.results);
    const selectedModule = allModulesArr.find((m) => m.Id === selectedModuleId);
    if (selectedModule) setModuleName(selectedModule.Module);

    computeUsedOrders(selectedModuleId, allRightsData);

    // Only auto-fill a suggestion for brand new rights, never overwrite an existing edit value
    if (!props.isEditing) {
      const siblings = allRightsData.filter((r) => r.ModuleId === selectedModuleId);
      const used = siblings
        .map((r) => r.DisplayOrder)
        .filter((n) => n !== null && n !== undefined);
      const next = used.length ? Math.max(...used) + 1 : 1;
      setFieldValue("displayOrder", next);
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const { title, icon, action, moduleId, displayOrder } = values;
      const parsedOrder = displayOrder !== "" ? parseInt(displayOrder) : null;

      if (parsedOrder !== null && usedDisplayOrders.includes(parsedOrder)) {
        const confirm = await Swal.fire({
          icon: "warning",
          title: "Number already in use",
          text: `Display order ${parsedOrder} is already used by another right in this module. It will be swapped with this one's current position. Continue?`,
          showCancelButton: true,
        });
        if (!confirm.isConfirmed) {
          setSubmitting(false);
          return;
        }
      }

      const updateModule = moduleName === "" ? props.data?.Module : moduleName;

      if (props.isEditing) {
        await updateRight({
          variables: {
            id: props.data.Id,
            title,
            icon,
            action,
            moduleId,
            displayOrder: parsedOrder,
            createdBy: userId,
            companyId: parseInt(companyId),
            module: updateModule,
            AdminOnly: 0,
          },
        });
      } else {
        await createRight({
          variables: {
            title,
            icon,
            action,
            moduleId,
            displayOrder: parsedOrder,
            module: updateModule,
            companyId: parseInt(companyId),
            createdBy: userId,
            AdminOnly: 0,
          },
        });
      }
    } catch (error) {
      console.error("Error:", error.message);
    }

    setSubmitting(false);
  };

  return (
    <Box m="20px">
      <Header
        title={props.isEditing ? "Edit Right" : "Create Right"}
        subtitle={
          props.isEditing ? "Edit an Existing Right" : "Create a New Right"
        }
      />

      <Formik
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
          setFieldValue,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box display="grid" gap="30px">
              <TextField
                fullWidth
                variant="filled"
                type="text"
                label="Title*"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.title}
                name="title"
                error={touched.title && !!errors.title}
                helperText={touched.title && errors.title}
                sx={{ gridColumn: "span 2" }}
              />
              <TextField
                fullWidth
                variant="filled"
                type="text"
                label="Icon *"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.icon}
                name="icon"
                error={touched.icon && !!errors.icon}
                helperText={touched.icon && errors.icon}
                sx={{ gridColumn: "span 2" }}
              />
              <TextField
                fullWidth
                variant="filled"
                type="text"
                label="Action *"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.action}
                name="action"
                error={touched.action && !!errors.action}
                helperText={touched.action && errors.action}
                sx={{ gridColumn: "span 2" }}
              />
              {modulesLoading ? (
                <div>
                  <CircularProgress />
                </div>
              ) : modulesError ? (
                <div>Error loading modules</div>
              ) : (
                <Select
                  fullWidth
                  variant="filled"
                  value={values.moduleId}
                  name="moduleId"
                  onChange={(e) => {
                    handleModuleChange(e, setFieldValue);
                    handleChange(e);
                  }}
                  onBlur={handleBlur}
                  error={touched.moduleId && !!errors.moduleId}
                  displayEmpty
                >
                  <MenuItem value="" disabled>
                    Select Module
                  </MenuItem>
                  {JSON.parse(modulesData.allModules.results).map((module) => (
                    <MenuItem key={module.Id} value={module.Id}>
                      {module.Module}
                    </MenuItem>
                  ))}
                </Select>
              )}

              <TextField
                fullWidth
                variant="filled"
                type="number"
                label={`Display Order (suggested: ${suggestedOrder})`}
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.displayOrder}
                name="displayOrder"
                error={touched.displayOrder && !!errors.displayOrder}
                helperText={
                  usedDisplayOrders.length
                    ? `Already used in this module: ${usedDisplayOrders.join(", ")}`
                    : "No display order set yet for this module's rights"
                }
                sx={{ gridColumn: "span 2" }}
              />
            </Box>
            <Box display="flex" justifyContent="end" mt="20px">
              <Button
                type="submit"
                color="primary"
                variant="contained"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Submit"}
              </Button>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default RightsForm;